package br.com.heyagenda.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import br.com.heyagenda.domain.util.SlugUtils
import br.com.heyagenda.ui.components.AuraButton
import br.com.heyagenda.ui.components.AuraCard
import br.com.heyagenda.ui.components.AuraPillBadge
import br.com.heyagenda.ui.components.AuraTextField
import br.com.heyagenda.ui.theme.AuraBackground
import br.com.heyagenda.ui.theme.AuraBorderSubtle
import br.com.heyagenda.ui.theme.AuraEmerald
import br.com.heyagenda.ui.theme.AuraPillBg
import br.com.heyagenda.ui.theme.AuraTextMuted
import br.com.heyagenda.ui.theme.AuraTextPrimary
import br.com.heyagenda.ui.theme.AuraTextSecondary
import cafe.adriel.voyager.core.screen.Screen
import heyagenda.shared.generated.resources.Res
import heyagenda.shared.generated.resources.link
import org.jetbrains.compose.resources.painterResource

data class LandingScreen(
    val onOpenBooking: (slug: String) -> Unit = {}
) : Screen {

    @Composable
    override fun Content() {
        var inputSlug by remember { mutableStateOf("") }
        var errorMessage by remember { mutableStateOf<String?>(null) }
        val scrollState = rememberScrollState()

        Scaffold(
            modifier = Modifier.fillMaxSize(),
            containerColor = AuraBackground
        ) { innerPadding ->
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(innerPadding)
                    .background(AuraBackground),
                contentAlignment = Alignment.TopCenter
            ) {
                val isMobile = maxWidth < 725.dp

                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .widthIn(max = 840.dp)
                        .padding(horizontal = if (isMobile) 16.dp else 24.dp)
                        .verticalScroll(scrollState),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Spacer(modifier = Modifier.height(if (isMobile) 20.dp else 32.dp))

                    // 1. Navigation Header
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Box(
                                modifier = Modifier
                                    .size(if (isMobile) 32.dp else 36.dp)
                                    .clip(RoundedCornerShape(10.dp))
                                    .background(AuraEmerald),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = "H",
                                    fontSize = if (isMobile) 18.sp else 20.sp,
                                    fontWeight = FontWeight.Black,
                                    color = AuraBackground
                                )
                            }
                            Spacer(modifier = Modifier.width(10.dp))
                            Text(
                                text = "Hey Agenda",
                                fontSize = if (isMobile) 18.sp else 20.sp,
                                fontWeight = FontWeight.Bold,
                                color = AuraTextPrimary
                            )
                        }

                        AuraPillBadge(
                            text = if (isMobile) "App & Web" else "Plataforma Web & App",
                            backgroundColor = AuraPillBg,
                            textColor = AuraEmerald
                        )
                    }

                    Spacer(modifier = Modifier.height(if (isMobile) 32.dp else 56.dp))

                    // 2. Hero Tag
                    AuraPillBadge(
                        text = "AGENDAMENTO ONLINE & GESTÃO INTELIGENTE",
                        backgroundColor = AuraEmerald.copy(alpha = 0.12f),
                        textColor = AuraEmerald
                    )

                    Spacer(modifier = Modifier.height(18.dp))

                    // 3. Hero Headline
                    Text(
                        text = "A experiência definitiva de agendamento para clientes e profissionais.",
                        fontSize = if (isMobile) 24.sp else 34.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = AuraTextPrimary,
                        textAlign = TextAlign.Center,
                        lineHeight = if (isMobile) 32.sp else 42.sp
                    )

                    Spacer(modifier = Modifier.height(14.dp))

                    // 4. Hero Subtitle
                    Text(
                        text = "Seus clientes escolhem serviços e horários em segundos pelo navegador sem precisar criar conta ou baixar apps. Você controla folgas, horários e faturamento no app nativo.",
                        fontSize = if (isMobile) 13.sp else 15.sp,
                        color = AuraTextMuted,
                        textAlign = TextAlign.Center,
                        lineHeight = if (isMobile) 20.sp else 24.sp,
                        modifier = Modifier.widthIn(max = 620.dp)
                    )

                    Spacer(modifier = Modifier.height(if (isMobile) 24.dp else 36.dp))

                    // 5. Quick Access to Professional Card
                    AuraCard(
                        modifier = Modifier
                            .fillMaxWidth()
                            .widthIn(max = 560.dp),
                        cornerRadius = 20.dp
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(if (isMobile) 18.dp else 24.dp)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    painter = painterResource(Res.drawable.link),
                                    contentDescription = null,
                                    tint = AuraEmerald,
                                    modifier = Modifier.size(16.dp)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    text = "Acessar Agenda de um Estúdio",
                                    fontSize = if (isMobile) 14.sp else 15.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = AuraTextPrimary
                                )
                            }

                            Spacer(modifier = Modifier.height(6.dp))
                            Text(
                                text = "Digite o link ou código personalizado do profissional",
                                fontSize = 12.sp,
                                color = AuraTextMuted
                            )

                            Spacer(modifier = Modifier.height(16.dp))

                            Text(
                                text = "Código ou Nome do Estúdio",
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Medium,
                                color = AuraTextSecondary,
                                modifier = Modifier.padding(bottom = 6.dp)
                            )

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Box(modifier = Modifier.weight(1f)) {
                                    AuraTextField(
                                        value = inputSlug,
                                        onValueChange = {
                                            inputSlug = it
                                            errorMessage = null
                                        },
                                        placeholder = "ex: barbearia-silva",
                                        modifier = Modifier.fillMaxWidth()
                                    )
                                }

                                Spacer(modifier = Modifier.width(10.dp))

                                AuraButton(
                                    text = "Acessar",
                                    onClick = {
                                        val clean = SlugUtils.toSlug(inputSlug)
                                        if (clean.isNotBlank()) {
                                            onOpenBooking(clean)
                                        } else {
                                            errorMessage = "Digite um link ou nome válido."
                                        }
                                    }
                                )
                            }

                            if (errorMessage != null) {
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = errorMessage ?: "",
                                    fontSize = 11.sp,
                                    color = Color(0xFFFF5252)
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(if (isMobile) 32.dp else 56.dp))

                    // 6. Value Proposition Cards (Column on mobile, Row on wide screens)
                    if (isMobile) {
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            FeatureCard(
                                icon = "⚡",
                                title = "Agendamento 24/7",
                                description = "Link exclusivo do estúdio para compartilhar no Instagram e WhatsApp.",
                                isMobile = true,
                                modifier = Modifier.fillMaxWidth()
                            )
                            FeatureCard(
                                icon = "🗓️",
                                title = "Horários Flexíveis",
                                description = "Controle dinâmico de pausas, folgas e exceções por data em tempo real.",
                                isMobile = true,
                                modifier = Modifier.fillMaxWidth()
                            )
                            FeatureCard(
                                icon = "📱",
                                title = "Gestão no Bolso",
                                description = "Aplicativo mobile completo para o profissional acompanhar cada atendimento.",
                                isMobile = true,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            FeatureCard(
                                icon = "⚡",
                                title = "Agendamento 24/7",
                                description = "Link exclusivo do estúdio para compartilhar no Instagram e WhatsApp.",
                                isMobile = false,
                                modifier = Modifier.weight(1f)
                            )
                            FeatureCard(
                                icon = "🗓️",
                                title = "Horários Flexíveis",
                                description = "Controle dinâmico de pausas, folgas e exceções por data em tempo real.",
                                isMobile = false,
                                modifier = Modifier.weight(1f)
                            )
                            FeatureCard(
                                icon = "📱",
                                title = "Gestão no Bolso",
                                description = "Aplicativo mobile completo para o profissional acompanhar cada atendimento.",
                                isMobile = false,
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(if (isMobile) 32.dp else 64.dp))

                    // 7. Footer
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .border(width = 0.5.dp, color = AuraBorderSubtle, shape = RoundedCornerShape(1.dp))
                            .padding(top = 24.dp, bottom = 32.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "Hey Agenda © 2026 • Gestão inteligente e agendamento online.",
                            fontSize = 12.sp,
                            color = AuraTextMuted,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun FeatureCard(
    icon: String,
    title: String,
    description: String,
    isMobile: Boolean = false,
    modifier: Modifier = Modifier
) {
    AuraCard(
        modifier = modifier,
        cornerRadius = 16.dp
    ) {
        if (isMobile) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(42.dp)
                        .background(AuraPillBg, RoundedCornerShape(12.dp)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = icon,
                        fontSize = 20.sp
                    )
                }
                Spacer(modifier = Modifier.width(14.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = title,
                        fontSize = 15.sp,
                        fontWeight = FontWeight.Bold,
                        color = AuraTextPrimary
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = description,
                        fontSize = 12.sp,
                        color = AuraTextMuted,
                        lineHeight = 17.sp
                    )
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(20.dp)
            ) {
                Text(
                    text = icon,
                    fontSize = 24.sp
                )
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = title,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.Bold,
                    color = AuraTextPrimary
                )
                Spacer(modifier = Modifier.height(6.dp))
                Text(
                    text = description,
                    fontSize = 12.sp,
                    color = AuraTextMuted,
                    lineHeight = 18.sp
                )
            }
        }
    }
}
